import React, { Component, MouseEvent, ReactNode } from 'react';
import { Group, User } from '../../../domain/rbac/RbacDomains';
import { ColumnProps } from 'primereact/column';
import CustomTable from '../../custom/CustomTable';
import Icon from '../../../icons/Icon';
import { DataTableRowClickEventParams } from 'primereact/datatable';
import { customConfirm } from '../../custom/CustomPopupConfirm';
import RoleHelper from '../../../RoleHelper';
import EnvironmentUtil from '../../../util/EnvironmentUtil';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import CustomTableContentWrapper from '../../custom/CustomTableContentWrapper';
import { Tooltip } from 'primereact/tooltip';
import moment from 'moment';
import { CheckerRequest } from '../../../domain/maker-checker/MakerCheckerDomains';
import MakerCheckerClient from '../../../client/maker-checker/MakerCheckerClient';
import GatewayContainer from '../../../config/GatewayContainer';
import GatewayNames from '../../../constant/GatewayNames';
import ComponentName from '../../../constant/ComponentName';
import LanguageUtil from '../../../util/LanguageUtil';

class GroupListComponent extends Component<Props> {
  private readonly environmentUtil: EnvironmentUtil = InstanceContainer.get(Injectables.ENVIRONMENT_UTIL);
  private readonly makerCheckerClient: MakerCheckerClient = GatewayContainer.getInstance().get(GatewayNames.MAKER_CHECKER_CLIENT);

  public render(): ReactNode {
    const { groups } = this.props;
    return <CustomTable selectionMode={'single'} onRowClick={this.openEditor} columns={this.getColumns()} value={groups} />;
  }

  private openEditor = (e: DataTableRowClickEventParams) => {
    const { setSelectedGroup, openEditor, setUpdateRequestData } = this.props;
    openEditor();
    setSelectedGroup(e.data);
    this.makerCheckerClient.findByDocumentId(ComponentName.ADMIN_GROUP, e.data.name).then(setUpdateRequestData);
  };

  private getColumns = (): ColumnProps[] => {
    const columns = [
      {
        field: 'name',
        header: LanguageUtil.get({ id: 'common.label.name', defaultMessage: 'Name' }),
        alignHeader: 'left',
        className: 'w-1/6',
        body: (record: Group) => {
          return (
            <div className={'flex w-full items-center'}>
              <CustomTableContentWrapper
                clipSize={15}
                showEllipsis={true}
                text={record.name} />
            </div>
          );
        },
      },
      {
        field: 'description',
        header: LanguageUtil.get({ id: 'common.label.groupDescription', defaultMessage: 'Group Description' }),
        className: 'w-1/6',
        body: (record: Group) => {
          return (
            <div className={'flex w-full items-center'}>
              <CustomTableContentWrapper
                clipSize={25}
                showEllipsis={true}
                text={record.description} />
            </div>
          );
        },
      },
      {
        field: 'roles',
        header: LanguageUtil.get({ id: 'common.label.roles', defaultMessage: 'Roles' }),
        className: 'w-1/6',
        body: (record: Group) => {
          return (
            <div className={'flex w-full gap-x-2 items-center'}>
              {record.roles &&
                record.roles.map((role, index) => {
                  return (
                    index < 3 && (
                      <div key={index} className={'flex gap-x-2'}>
                        <CustomTableContentWrapper text={role} />
                      </div>
                    )
                  );
                })}
              {record.roles && record.roles.length > 3 && <p>...</p>}
            </div>
          );
        },
      },
      {
        field: 'createdAt',
        header: LanguageUtil.get({ id: 'common.label.createdAt', defaultMessage: 'Created At' }),
        className: 'w-1/6',
        body: (record: Group) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper text={record.createdAt ? moment(record.createdAt).format('LLL') : ''} />
            </div>
          );
        },
      },
      {
        field: 'updatedAt',
        header: LanguageUtil.get({ id: 'common.label.updatedAt', defaultMessage: 'Updated At' }),
        className: 'w-1/6',
        body: (record: Group) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper text={record.updatedAt ? moment(record.updatedAt).format('LLL') : ''} />
            </div>
          );
        },
      },
      {
        header: LanguageUtil.get({ id: 'common.label.action', defaultMessage: 'Action' }),
        field: 'action',
        className: 'max-w-[75px] 2xl:max-w-[100px]',
        sortable: false,
        hidden: this.environmentUtil.isLdapOnly(),
        body: (record: Group) => {
          const { checkerRequests } = this.props;
          const request = checkerRequests.find((c) => c.documentId == record.name);
          if (request && request.type == 'DELETE') {
            return;
          }

          return (
            <div
              className={'flex w-full justify-start items-center text-xs 2xl:text-md ml-2'}
              onClick={(e: MouseEvent<HTMLDivElement>) => {
                if (!RoleHelper.canManage('Group')) return;
                e.stopPropagation();
                e.preventDefault();
                this.onDeleteGroup(e, record);
              }}
            >
              <Tooltip position={'left'} target={'.remove'}>
                {LanguageUtil.get({ id: 'common.label.remove', defaultMessage: 'Remove' })}
              </Tooltip>
              <Icon icon={'delete'} size={20} className={'remove text-[#607080] hover:cursor-pointer'} />
            </div>
          );
        },
      },
    ];
    return columns.filter((column) => {
      if (column.field === 'action') {
        return RoleHelper.canManage('Group');
      }
      return true;
    }) as ColumnProps[];
  };

  private onDeleteGroup = (e: MouseEvent<HTMLDivElement>, record: Group) => {
    const { onDeleteGroup } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: () => onDeleteGroup(record.name),
      message: LanguageUtil.get({ id: 'messages.confirm.deleteGroup', defaultMessage: 'Do you sure delete this group?' }),
      acceptClassName: '!bg-primary !text-2xs',
    });
  };
}

interface Props {
  groups: Group[];
  selectedGroup: Group | undefined;
  openEditor: VoidFunction;
  setSelectedGroup: (group: Group) => void;
  onDeleteGroup: (name: string) => void;
  checkerRequests: CheckerRequest[];
  updateRequestData: CheckerRequest<User> | undefined;
  setUpdateRequestData: (request: CheckerRequest | undefined) => void;
}

export default GroupListComponent;
